import {Button, Icon, Tooltip} from '@startlibs/components';
import React from 'react'
import {
  CASE_CLOSED,
  CASE_DRAFT,
  CASE_REVIEWED,
  PENDING_ASSIGNMENT,
  PENDING_REQUEST,
  UNDER_REVIEW,
  WAITING_ACCEPTANCE,
  WAITING_APPROVAL,
  WAITING_MEDICAL_RECORDS,
  WAITING_MORE_INFORMATION
} from '../enums/CaseState'
import {ScrollToTop} from '../components/ScrollToTop'
import {
  CaseId, CollapsedSidebarIcon,
  Sidebar,
  SidebarCard,
  SidebarContainer,
  SidebarDesc,
  SidebarInformation,
  SidebarSeparator,
  SidebarTitle,
  StepLink,
} from '../components/Sidebar';
import { IfLocale } from '../hocs/IfLocale';
import styled from 'styled-components';
import { getColor, isSmallDevice } from '@startlibs/utils'
import { useToggle, WithLazyResource } from '@startlibs/core'
import { ListExpertsDialog } from './dialogs/ListExpertsDialog';
import {lazyProviderInfo, lazyUserInfo} from '../components/WithProvider';
import {FormattedMessage, useIntl} from "react-intl";
import {LocaleDisplay} from "../components/LocaleSelector";
// import {useMatch} from "react-router";
import { useMatch } from '../hooks/useMatch';
import {useMobileSidebar} from "../components/Sidebar";
import { HIDDEN } from '../utils/utils';


const ReadOnlyCard = styled.div`  
  background-color: #F8F2DF;
  color: rgba(0,0,0,0.6);
  font-weight: 600;
  border-radius: 6px;
  font-size: 14px;
  margin: -0.5rem 0.5rem 1rem;
  padding: 1.25rem;
  p {
    font-size: 11px;
    font-weight: 400;
    margin: 0.5rem 0 0;
  }
  ${Icon} {
    font-size: 22px;
    line-height: 0;
    vertical-align: -4px;
    margin-right: 0.25rem;
  }
`

const AssignedExpertInfo = styled(SidebarInformation)`
  padding-bottom: 0.25rem;
  &.expertName {
    font-size: 14px;
  }
  a {
    color: ${getColor('gray60')};
    
    :hover {
      color: ${getColor('gray30')};
    }
  }
`
const CoReviewersButton = styled(Button)`
  color: ${getColor('main')};
  font-weight: 600;
  box-shadow: none;
`

const STATE_LIST = [PENDING_REQUEST,CASE_DRAFT,WAITING_MEDICAL_RECORDS,WAITING_APPROVAL,PENDING_ASSIGNMENT,WAITING_ACCEPTANCE,UNDER_REVIEW,WAITING_MORE_INFORMATION,CASE_REVIEWED, CASE_CLOSED]

const isBefore = (state) => of => STATE_LIST.indexOf(state) < STATE_LIST.indexOf(of)



export const ExpertRequestSidebar = ({caseRequest = {}}) => {
  const isStateBefore = isBefore(caseRequest && caseRequest.state)
  const listExpertsPopup = useToggle()
  const intl = useIntl()
  const userInfo = lazyUserInfo.read()

  const match = useMatch("/expert/case/:requestId/:step")

  const step = match?.params && match.params.step
  const [isCollapsed, expandedToggle] = useMobileSidebar(step)

  const providerInfo = lazyProviderInfo.read()
  const requestCodeForExpert = providerInfo?.requestCodeForExpert || HIDDEN // SHOW|HIDDEN|MASKED

  const sidebarHeader = <>
  <SidebarTitle className='fs-exclude'>
    <FormattedMessage
      defaultMessage="{firstName} {middleName} {lastName}"
      description="Expert Sidebar patient name"
      values={{
        firstName: caseRequest.patientInfo.firstName,
        middleName: caseRequest.patientInfo.middleName,
        lastName: caseRequest.patientInfo.lastName
      }}
    />
    </SidebarTitle>
    {requestCodeForExpert !== HIDDEN && caseRequest.requestCode && <CaseId><b><FormattedMessage
      defaultMessage="Case ID: " 
      description='CaseRequestCard admin caseid item label'
    /></b> <span data-testid='case-id'>{caseRequest.requestCode}</span></CaseId>}
    <CaseId>
      <WithLazyResource value={lazyProviderInfo}>{providerInfo =>
        <LocaleDisplay locale={caseRequest.caseInfo.preferredLanguage} providerInfo={providerInfo} />
      }</WithLazyResource>
    </CaseId>
  </>
  

  return  <SidebarContainer collapsed={isCollapsed} onClick={e => e.target === e.currentTarget && expandedToggle.close()}>
    {
      listExpertsPopup.isOpen && <ListExpertsDialog listExpertsPopup={listExpertsPopup} coReviewersList={caseRequest.coReviewers} userInfo={userInfo} />
    }
    <ScrollToTop when={step}/>
    <Sidebar key="sidebar">
      <SidebarCard
        isPriority={caseRequest.markedAsPriorityEpochMilli}
        isExpert
        onClick={isCollapsed ? expandedToggle.open : undefined}
        collapsed={isCollapsed && sidebarHeader}
      >
        {caseRequest?.isCoReview &&
          <ReadOnlyCard>
            <Icon icon="view"/><FormattedMessage defaultMessage="Read-only access" description="Expert Sidebar, case read-only access label"/>
            <p><FormattedMessage
              defaultMessage="This case has been shared with you for informational purposes only"
              description="Expert Sidebar, case read-only access description"
            /></p>
          </ReadOnlyCard>
        }
        {isSmallDevice() && <CollapsedSidebarIcon icon="x" onClick={expandedToggle.close}/>}
        <div className="sidebar-heading">
          <SidebarDesc><FormattedMessage
            defaultMessage="CASE DETAILS"
            description="Expert Sidebar title"
          /></SidebarDesc>
          {sidebarHeader}
          <IfLocale contains="LEGAL">
            {caseRequest?.legalInfo?.caseNumber?.length > 0
              ? <CaseId><b>Case Number:</b> {caseRequest.legalInfo.caseNumber}</CaseId>
              : null
            }
          </IfLocale>
          {caseRequest.isCoReview && caseRequest.leadExpert && <>
            <SidebarDesc><FormattedMessage
              defaultMessage="ASSIGNED TO"
              description="Expert Sidebar assigned expert label"
            /></SidebarDesc>
            <AssignedExpertInfo className="expertName"><b><FormattedMessage
                defaultMessage="{firstName} {lastName}"
                description="Expert Sidebar assigned expert name"
                values={{
                  firstName: caseRequest.leadExpert?.firstName,
                  lastName: caseRequest.leadExpert?.lastName
                }}
              /></b></AssignedExpertInfo>
            <AssignedExpertInfo><a href={"mailto:"+caseRequest.leadExpert?.email} target="_blank" rel="noreferrer">{caseRequest.leadExpert?.email}</a></AssignedExpertInfo>
          </>}
          {
            <div className="sidebar-icons">
              {caseRequest.coReviewers && caseRequest.coReviewers.length > 0 && <>
                <CoReviewersButton small color="#e7f0f5" onClick={()=> listExpertsPopup.open()} icon="users-line">{caseRequest.coReviewers.length}</CoReviewersButton>
              </>}
              {caseRequest.markedAsPriorityEpochMilli && <div className="priority-icon" onClick={e=> {e.preventDefault();e.stopPropagation()}}>
                <Tooltip content={intl.formatMessage({
                  defaultMessage:"This case was marked as priority.",
                  description:"Expert Sidebar, case marked as priority tooltip"
                })} instant>
                  <Icon icon="star"/>
                </Tooltip>
              </div>}
            </div>
          }
        </div>
        <SidebarSeparator/>
        <StepLink
          to="remote-consultation"
          selected={step==="remote-consultation"}
        ><Icon icon="video"/> <FormattedMessage
          defaultMessage="Video consultation"
          description="Expert Sidebar, video consultation step label"
        /></StepLink>
        <SidebarSeparator/>
        <StepLink
          to="overview"
          selected={step==="overview"}
        ><Icon icon="clinical-sheet"/> <FormattedMessage
          defaultMessage="Case overview"
          description="Expert Sidebar, case overview step label"
        /></StepLink>
        {!caseRequest.isCoReview && <StepLink
          to="questions"
          disabled={isStateBefore(UNDER_REVIEW) && !caseRequest.readOnly}
          selected={step==="questions"}
        ><Icon icon="question-line"/> {caseRequest.isCoReview
          ? <FormattedMessage
            defaultMessage="Questions"
            description="Expert Sidebar, questions step label"
          />
          : <FormattedMessage
            defaultMessage="Expert review"
            description="Expert Sidebar, expert review step label"
          />}</StepLink>}

        {!caseRequest.isCoReview && <StepLink
          to="report"
          disabled={isStateBefore(UNDER_REVIEW)}
          selected={step === "report" || step === "upload-report"}
        >
          {caseRequest.state === CASE_CLOSED ?
            <><Icon icon="checked-report"/> <FormattedMessage
              defaultMessage="Case report"
              description="Expert Sidebar, case report step label"
            /></>
            : <><Icon icon="report"/> <FormattedMessage
              defaultMessage="Report preview"
              description="Expert Sidebar, report preview step label"
            />
            </>
          }
        </StepLink>}
      </SidebarCard>
    </Sidebar>
  </SidebarContainer>
}
